import { useApolloClient } from '@apollo/client'
import { AuthContext } from 'contexts'
import { useFormik } from 'formik'
import { useModal } from 'hooks'
import React, { useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
    usePayWithCardService,
    useAddCardService,
    useRemovePaymentCardService,
    useGetBalanceService,
    usePaymentDetails,
    useUpdateAutoPaySettingsService,
    useCustomerIdService,
    useCreateStripeCustomerService,
} from 'services'

const usePayment = () => {
    const { account } = useContext(AuthContext)
    const client = useApolloClient()
    const navigate = useNavigate()
    const location = useLocation()
    const [amount, setAmount] = React.useState('25')
    const { openModal } = useModal()

    const { payWithCard, loading: payWithCardLoading } = usePayWithCardService()
    const { createCard, loading: createCardLoading } = useAddCardService()
    const { removePaymentCard, loading: removePaymentCardLoading } = useRemovePaymentCardService()
    const { data: balance, loading: balanceLoading } = useGetBalanceService()
    const { data: payment_details } = usePaymentDetails()
    const { updateAutoPaySettings, loading: updateAutoPaySettingsLoading } =
        useUpdateAutoPaySettingsService()

    const { createStripeCustomer, loading: createStripeCustomerLoading } =
        useCreateStripeCustomerService()

    const { data: customerId, loading: customerIdLoading } = useCustomerIdService({
        account_id: account.id,
    })

    const sessionId = new URLSearchParams(location.search).get('session_id')
    const cardSessionId = new URLSearchParams(location.search).get('card_session_id')

    const payWithCardHandler = async () => {
        const result = await payWithCard(Number(amount))
        window.location.href = result.checkout_url
    }

    const payWithTAOHandler = async () => {
        const appId = import.meta.env.REACT_APP_PAY_APP_ID

        let stripeCustomerId = customerId?.customer_id

        if (!stripeCustomerId) {
            const result = await createStripeCustomer()
            stripeCustomerId = result?.stripe_customer_id
        }

        window.location.href = `${
            import.meta.env.REACT_APP_PAY_APP_URL
        }/?amount=${amount}&app_id=${appId}&customer_id=${stripeCustomerId}`
    }

    const addCardHandler = async () => {
        const result = await createCard()
        window.location.href = result.url
    }

    const removeCardHandler = async () => {
        await removePaymentCard()
        client.refetchQueries({
            include: ['paymentDetails'],
        })
    }

    React.useEffect(() => {
        client.refetchQueries({
            include: ['account'],
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    React.useEffect(() => {
        if (cardSessionId) {
            // finishSetupHandler()
            navigate(`/billing`)
            openModal({ name: 'success-payment-modal' })
        }

        if (sessionId) {
            navigate(`/billing`)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, cardSessionId])

    const formik = useFormik({
        initialValues: {
            auto_pay_threshold: payment_details?.auto_pay_threshold,
            auto_pay_amount: payment_details?.auto_pay_amount,
        },
        enableReinitialize: true,
        onSubmit: values => handleSubmit(values),
    })

    async function handleSubmit(values: { auto_pay_threshold: number; auto_pay_amount: number }) {
        updateAutoPaySettings({
            auto_pay_threshold: Number(values.auto_pay_threshold),
            auto_pay_amount: Number(values.auto_pay_amount),
        })
    }

    return {
        payWithCardHandler,
        addCardHandler,
        balance,
        removePaymentCard: removeCardHandler,
        is_card_added: payment_details.is_card_added,
        amount,
        setAmount,
        formik,
        payWithCardLoading,
        createCardLoading,
        removePaymentCardLoading,
        balanceLoading: !balance && balanceLoading,
        updateAutoPaySettingsLoading,
        credits: account.credits,
        payWithTAOHandler,
        taoLoading: (!customerId && customerIdLoading) || createStripeCustomerLoading,
    }
}

export default usePayment
